import { useCallback, useEffect, useRef } from 'react';
import ResizeObserver from 'resize-observer-polyfill';

type UseModalResizeObserverParams = {
  enabled?: boolean;
  isOpen?: boolean;
  rootRef: React.RefObject<HTMLElement>;
  modalSelector?: string;
};

const DEFAULT_MODAL_SELECTOR = '.ncoded-modal__content';

const useModalResizeObserver = ({
  enabled = true,
  isOpen = false,
  rootRef,
  modalSelector = DEFAULT_MODAL_SELECTOR,
}: UseModalResizeObserverParams) => {
  const rafIdRef = useRef<number | null>(null);

  const dispatchReposition = useCallback(() => {
    window.dispatchEvent(new Event('scroll'));
    window.dispatchEvent(new Event('resize'));
  }, []);

  const onResize = useCallback(() => {
    if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

    rafIdRef.current = requestAnimationFrame(dispatchReposition);
  }, [dispatchReposition]);

  useEffect(() => {
    const rootEl = rootRef.current;

    if (!enabled || !isOpen || !rootEl) return;

    const modalContent = rootEl.closest(modalSelector);

    if (!modalContent) return;

    const observer = new ResizeObserver(onResize);

    observer.observe(modalContent);

    return () => {
      if (rafIdRef.current) cancelAnimationFrame(rafIdRef.current);

      observer.disconnect();
    };
  }, [enabled, isOpen, modalSelector, onResize, rootRef]);
};

export default useModalResizeObserver;
